import { Skeleton, tableHelpers, lucideIcons, Tooltip, FlagIcon } from '@round/creatorbase-ui';
import { getMeta, CellContext } from '../../helpers';
import { getCountry } from 'utility/utility';
import styles from './LocationCell.module.css';
import { toDecimalPoint } from '@round/utils';
import { influencer } from '@round/api';

const { MapPinIcon, UsersIcon } = lucideIcons;

const TOP_AUDIENCE_COUNTRY_COUNT = 3;

export const LocationCell = ({ row: { original }, table }: CellContext) => {
    const isBaseLoading = tableHelpers.isLoadingRow(original) || getMeta(table).isLoading;

    if (isBaseLoading) {
        return (
            <div className={styles.container}>
                <p className={styles.row}>
                    <Skeleton className={styles.iconSkeleton} />
                    <Skeleton className={styles.flagSkeleton} />
                    <Skeleton className={styles.labelSkeleton} />
                </p>
                <p className={styles.row}>
                    <Skeleton className={styles.iconSkeleton} />
                    <Skeleton className={styles.flagSkeleton} />
                    <Skeleton className={styles.flagSkeleton} />
                    <Skeleton className={styles.flagSkeleton} />
                </p>
            </div>
        );
    }
    const { country, label: locationLabel } = getCreatorLocation(original);
    const audienceCountries = getTopAudienceCountries(original);

    return (
        <div className={styles.container}>
            <Tooltip open={!country ? false : undefined}>
                <Tooltip.Trigger asChild>
                    <p className={styles.row}>
                        <MapPinIcon className={styles.icon} aria-hidden />
                        {country ? (
                            <>
                                <FlagIcon className={styles.flag} countryCode={country.code} />
                                <span className={styles.label}>{country.label}</span>
                            </>
                        ) : (
                            <span className={styles.label}>{locationLabel}</span>
                        )}
                    </p>
                </Tooltip.Trigger>
                <Tooltip.Content side="bottom" className={styles.tooltip}>
                    <p className={styles.tooltipHeader}>Creator location</p>
                    {locationLabel}
                </Tooltip.Content>
            </Tooltip>

            <Tooltip>
                <Tooltip.Trigger asChild>
                    <p className={styles.row}>
                        <UsersIcon className={styles.icon} aria-hidden />
                        {audienceCountries.length
                            ? audienceCountries.map((c) => (
                                  <FlagIcon key={c.code} className={styles.flag} countryCode={c.code} />
                              ))
                            : '-'}
                    </p>
                </Tooltip.Trigger>
                <Tooltip.Content side="bottom" className={styles.tooltip}>
                    <p className={styles.tooltipHeader}>Top Audience</p>
                    {audienceCountries.map((c) => {
                        const audienceCountry = getCountry(c.code);
                        const label = audienceCountry?.label ?? c.code;
                        return (
                            <p key={c.code} className={styles.tooltipRow}>
                                <span className={styles.tooltipCountry}>
                                    <FlagIcon className={styles.flag} countryCode={c.code} />
                                    {label}
                                </span>
                                <span className={styles.tooltipValue}>{toDecimalPoint(c.value, 2)}%</span>
                            </p>
                        );
                    })}
                </Tooltip.Content>
            </Tooltip>
        </div>
    );
};

/** Shared by the cell and the expanded row's details panel. `country` is only set for a code we can name. */
export const getCreatorLocation = (creator: influencer.CreatorSearchResult) => {
    const country = getCountry(creator.primary_country);
    return { country, label: country?.label || creator.primary_country || '-' };
};

export const getTopAudienceCountries = (creator: influencer.CreatorSearchResult) =>
    creator.top_audience_countries?.slice(0, TOP_AUDIENCE_COUNTRY_COUNT) ?? [];
